import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAdmin } from "@/admin/AdminContext";
import { PageHeader } from "@/admin/ui";
import { money } from "@/lib/brand";
import { Users, Repeat, TrendingUp, Clock, DollarSign, Activity, UserCheck, CalendarClock, AlertTriangle } from "lucide-react";

const KPIS = [
  { key: "total_clients", label: "Total Clients", icon: Users, money: false, testid: "kpi-total-clients" },
  { key: "active_paid_customers", label: "Active Paid Customers", icon: UserCheck, money: false, testid: "kpi-active-customers" },
  { key: "active_subscriptions", label: "Active Subscriptions", icon: Repeat, money: false, testid: "kpi-active-subs" },
  { key: "mrr", label: "Monthly Recurring Revenue", icon: TrendingUp, money: true, testid: "kpi-mrr" },
  { key: "total_collected", label: "Total Collected", icon: DollarSign, money: true, testid: "kpi-collected" },
  { key: "upcoming_renewals", label: "Upcoming Renewals (14d)", icon: CalendarClock, money: false, testid: "kpi-renewals" },
  { key: "pending_payments", label: "Pending Payments", icon: Clock, money: false, testid: "kpi-pending" },
  { key: "past_due", label: "Past Due", icon: AlertTriangle, money: false, testid: "kpi-past-due" },
  { key: "failed_payments", label: "Failed Payments", icon: AlertTriangle, money: false, testid: "kpi-failed" },
];

const ACTION_LABEL = {
  "client.create": "New client added",
  "client.update": "Client updated",
  "client.archive": "Client archived",
  "service.create": "Service created",
  "service.archive": "Service archived",
  "payment_request.create": "Payment link generated",
  "subscription.cancel": "Subscription canceled",
  "team.invite": "Team member invited",
  "team.update_permissions": "Permissions updated",
  "team.remove": "Team member removed",
};

export default function Dashboard() {
  const { version } = useAdmin();
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState([]);

  const loadAlerts = () => api.get("/billing/alerts?resolved=false").then((r) => setAlerts(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
    loadAlerts();
  }, [version]);

  const resolveAlert = (id) => api.post(`/billing/alerts/${id}/resolve`).then(() => loadAlerts()).catch(() => {});

  const SEV = {
    critical: "bg-red-50 border-red-200 text-red-700",
    high: "bg-orange-50 border-orange-200 text-orange-700",
    medium: "bg-amber-50 border-amber-200 text-amber-700",
  };

  return (
    <div className="animate-fade-up">
      <PageHeader title="Dashboard" subtitle="Your business at a glance" />
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {KPIS.map((k) => (
          <div key={k.key} data-testid={k.testid}
            className="bg-white border border-[#E2EBF0] rounded-xl p-4 sm:p-5 relative overflow-hidden">
            <div className="absolute top-0 left-0 h-full w-1 bg-[#67F2B3]" />
            <k.icon className="h-5 w-5 text-[#10B981]" />
            <div className="mt-3 font-mono font-bold text-xl sm:text-2xl text-[#0D3B2D]" data-testid={`${k.testid}-value`}>
              {data ? (k.money ? money(data[k.key]) : data[k.key]) : "—"}
            </div>
            <div className="text-xs text-[#4A635A] mt-1 leading-tight">{k.label}</div>
          </div>
        ))}
      </div>

      {alerts.length > 0 && (
        <div className="mt-6 bg-white border border-[#E2EBF0] rounded-xl p-5" data-testid="billing-alerts-panel">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="h-4 w-4 text-red-500" />
            <h2 className="font-heading font-semibold text-[#0D3B2D]">Billing Alerts</h2>
            <span className="text-xs font-mono text-red-600" data-testid="billing-alerts-count">{alerts.length}</span>
          </div>
          <ul className="space-y-2" data-testid="billing-alerts-list">
            {alerts.map((a) => (
              <li key={a.alert_id} data-testid={`alert-${a.alert_id}`}
                className={`flex items-center justify-between gap-3 border rounded-lg px-3 py-2 text-sm ${SEV[a.severity] || "bg-slate-50 border-slate-200 text-slate-700"}`}>
                <div className="min-w-0">
                  <span className="font-medium">{a.client_name}</span>
                  <span className="opacity-80"> · {a.message}</span>
                </div>
                <button onClick={() => resolveAlert(a.alert_id)} data-testid={`resolve-alert-${a.alert_id}`}
                  className="shrink-0 text-xs underline hover:no-underline">Resolve</button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-6 bg-white border border-[#E2EBF0] rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <Activity className="h-4 w-4 text-[#10B981]" />
          <h2 className="font-heading font-semibold text-[#0D3B2D]">Recent Activity</h2>
        </div>
        {data && data.recent_activity.length > 0 ? (
          <ul className="divide-y divide-[#E2EBF0]" data-testid="recent-activity-list">
            {data.recent_activity.map((e) => (
              <li key={e.event_id} className="py-2.5 flex items-center justify-between text-sm">
                <span className="text-[#0D3B2D]">{ACTION_LABEL[e.action] || e.action}</span>
                <span className="text-xs text-[#4A635A] font-mono">{new Date(e.created_at).toLocaleString()}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-[#4A635A]">No activity yet. Create your first client and service to get started.</p>
        )}
      </div>
    </div>
  );
}
